/**
 * DashboardDataContext — scoped data plane for the CAIO / CEO / Commercial
 * dashboards (Wave 29).
 *
 * Pulls the aggregate `/api/dashboard` payload via `useDashboard()` and
 * exposes the SAME shape the panels previously consumed via
 * `useTenantData()`. This means the panels can keep their hook usage
 * unchanged — only the import switches from `useTenantData` to
 * `useDashboardData`.
 *
 * Why a scoped context (not just prop drilling)?
 *   - Panels are deeply nested (CaioPanels exports 11 components),
 *     drilling 8-12 props each would noisy up the layout.
 *   - The overlay (optimistic opportunities/requests/audit entries from
 *     ApiTenantContext) needs to merge on top of REST data — the
 *     context layers that merge once at the page level, so each panel
 *     receives "live" totals without re-doing the merge.
 *   - Each dashboard page (CAIO/CEO/Commercial) gets its OWN provider
 *     so refetching one doesn't cascade into the others.
 */
import React, { createContext, useContext, useMemo } from "react";
import { useDashboard } from "@/lib/useDashboard";
import { useTenantData } from "@/contexts/TenantModeContext";

const DashboardDataContext = createContext(null);

// Same empty shape the bundle endpoint returns for customer-mode tenants.
const EMPTY_DASHBOARD = {
  tenant: null,
  kpis: {
    governedAgents: 0,
    totalAgents: 0,
    monthlySpendAud: 0,
    monthlyValueAud: 0,
    roiRatio: 0,
    openHighIncidents: 0,
    violations: 0,
    ungoverned: 0,
    pendingApprovals: 0,
    avgComplianceCoverage: 0,
  },
  trend12m: [],
  topBurners: [],
  departmentRollups: [],
  vendorRollups: [],
  vendorBurnRollup: [],
  frameworks: [],
  reports: [],
  benchmark: null,
  costOpportunities: [],
  topOpportunities: [],
  pendingRequests: [],
  auditRecent: [],
  shadowAgents: [],
  openIncidents: [],
  policies: [],
  isEmpty: true,
  flywheel: { opportunitiesTotal: 0, requestsInFlight: 0, agentsLive: 0 },
  systemStatus: null,
};

/**
 * Merge optimistic overlay (from ApiTenantContext) on top of the
 * server's aggregate response. Only slices that mutations create live
 * are merged (opportunities, requests, audit entries).
 */
function mergeOverlay(data, overlay) {
  if (!data || !overlay) return data;
  const pendingOpps = overlay.opportunities || [];
  const pendingReqs = overlay.requests || [];
  const pendingAudit = overlay.auditEntries || [];

  // Top opportunities: overlay records may displace server top-5 if
  // their estAnnualRoiAud is higher. Dedupe by id.
  const mergedOpps = [...pendingOpps, ...(data.topOpportunities || [])];
  const seenOppIds = new Set();
  const dedupedOpps = mergedOpps.filter((o) => {
    if (seenOppIds.has(o.id)) return false;
    seenOppIds.add(o.id);
    return true;
  });
  const topOpportunities = [...dedupedOpps]
    .sort((a, b) => (b.estAnnualRoiAud || 0) - (a.estAnnualRoiAud || 0))
    .slice(0, 5);

  // Pending requests: prepend overlay records, dedupe by id, slice 4.
  const mergedReqs = [...pendingReqs, ...(data.pendingRequests || [])];
  const seenReqIds = new Set();
  const pendingRequests = mergedReqs
    .filter((r) => {
      if (seenReqIds.has(r.id)) return false;
      seenReqIds.add(r.id);
      return true;
    })
    .filter((r) => (r.stage || 0) < 5)
    .slice(0, 4);

  // Audit ticker: overlay entries are newer, surface them first.
  const auditRecent = [...pendingAudit, ...(data.auditRecent || [])].slice(0, 6);

  return { ...data, topOpportunities, pendingRequests, auditRecent };
}

export function DashboardDataProvider({ children }) {
  const { data, loading, error, lastSyncedAt, refetch } = useDashboard();
  // Overlay slice — optimistic mutations live here until the next refetch.
  const tenantCtx = useTenantData();
  const overlay = tenantCtx?.pendingEntities;

  const value = useMemo(() => {
    const merged = data ? mergeOverlay(data, overlay) : EMPTY_DASHBOARD;
    // For panels that still read `agents` (used only for shadow filtering)
    // — provide the shadow slice under the legacy key so they can keep
    // their `agents.filter(...)` calls until a future polish wave.
    return {
      ...merged,
      // Compatibility aliases used by some panels' bundle-era code:
      agents: merged.shadowAgents || [],  // panels only ever filter `status === 'ungoverned'`
      opportunities: merged.topOpportunities || [],
      requests: merged.pendingRequests || [],
      incidents: merged.openIncidents || [],
      auditEntries: merged.auditRecent || [],
      loading,
      error,
      lastSyncedAt,
      refetch,
    };
  }, [data, overlay, loading, error, lastSyncedAt, refetch]);

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
}

export function useDashboardData() {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) {
    throw new Error(
      "useDashboardData must be used inside a DashboardDataProvider",
    );
  }
  return ctx;
}
